import React, {
  useState,
  useEffect,
  useCallback,
  memo,
  useMemo,
  useRef,
} from "react";
import { debounce } from "lodash";
import axios from "axios";
import {
  FaClock,
  FaProjectDiagram,
  FaShapes,
  FaTools,
  FaDatabase,
} from "react-icons/fa";
import SourceLineChart from "./SourceLineChart";
import CustomTooltip from "../Buttons/CustomTooltip";
import RadarChartForConversion from "../migrations/RadarChartForConversion";
import SourcePieChart from "./SourcePieChart";

const EmptyState = ({ label }) => (
  <div
    className="text-center py-4"
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      flex: 1,
    }}
  >
    <img
      src="assets/images/emptystate.gif"
      style={{ width: "200px", opacity: 0.7 }}
      alt="No Data Available"
    />
    <p className="text-muted mt-2">No data available</p>
  </div>
);

const RecentActivities = ({ activities = [], loading = false }) => {
  const truncateText = useCallback((text, maxLength) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  }, []);

  const showEmptyState = useMemo(
    () => loading || !activities || activities.length === 0,
    [loading, activities]
  );

 return (
  <div
    className="border-0 card-migration cursor-pointer"
    style={{
      marginBottom: "2px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}
  >
    <div
      className="card-body p-3 d-flex flex-column"
      style={{ height: "100%" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center">
          <div className="icons-bg me-2">
            <i className="dash-icon-recent-code"></i>
          </div>
          <h6 className="dbcardheadsdesign mb-0">Recent Activities</h6>
        </div>
        
        {activities.length > 0 && !loading && (
          <h5
            className="card-title fw-bold mb-0"
            style={{ fontSize: "11px", textDecoration: "underline" }}
          >
            <a
              href="/sourceprofiler"
              style={{ color: "inherit", textDecoration: "inherit" }}
            >
              View All
            </a>
          </h5>
        )}
      </div>

      <div
        className={`flex-grow-1 d-flex flex-column ${
          !activities.length && !loading
            ? "align-items-center justify-content-center"
            : ""
        }`}
        style={{
          minHeight: "270px",
          overflowY: "auto",
          marginTop: "10px",
        }}
      >
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border spinner-border-sm text-secondary"></div>
          </div>
        ) : showEmptyState ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
            <img
              src="assets/images/emptystate.gif"
              style={{ width: "180px", opacity: 0.7, marginBottom: "0px" }}
              alt="No activities found"
            />
            <p className="text-muted mb-0">No recent activities found</p>
          </div>
        ) : (
          <div>
            {activities.map((activity, idx, arr) => (
              <div
                key={`${activity.event_time}-${idx}`}
                className="d-flex"
                style={{ marginBottom: "20px" }}
              >
                <div
                  style={{
                    width: "40px",
                    position: "relative",
                    top: "5px",
                  }}
                >
                  <div class="icons-bg3 me-2"><i class="dash-icon-database"></i></div>
                  {idx !== arr.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "25px",
                        bottom: "-20px",
                        left: "10.5px",
                        width: "2px",
                        backgroundColor: "#ECEAEE",
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    paddingLeft: "0px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    marginTop: "5px",
                  }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{
                      justifyContent: "space-between",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    {activity.event_header?.length > 15 ? (
                      <CustomTooltip content={activity.event_header} placement="left">
                        <strong className="stat-text2"
                          style={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "13px",
                            maxWidth: "110px",
                            minWidth: "110px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer"
                          }}
                        >
                          {truncateText(activity.event_header, 15)}
                        </strong>
                      </CustomTooltip>
                    ) : (
                      <strong className="stat-text2"
                        style={{
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: "13px",
                          maxWidth: "110px",
                          minWidth: "110px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {activity.event_header}
                      </strong>
                    )}

                    <div
                      className="d-flex align-items-center text-muted"
                      style={{
                        fontSize: "12px",
                        minWidth: "135px",
                        justifyContent: "flex-end",
                        marginLeft: "auto", 
                        alignItems: "center",
                      }}
                    >
                      <i
                        className="ri-calendar-2-line"
                        style={{ fontSize: "14px", marginRight: "4px" }}
                      ></i>
                      {activity.event_time}
                    </div>
                  </div>

                  {activity.event_message?.length > 28 ? (
                    <CustomTooltip content={activity.event_message} placement="right">
                      <small
                        className="text-muted two-line-text"
                        style={{
                          fontSize: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer"
                        }}
                      >
                        {truncateText(activity.event_message, 25)}
                      </small>
                    </CustomTooltip>
                  ) : (
                    <small
                      className="text-muted two-line-text"
                      style={{
                        fontSize: "12px",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {activity.event_message}
                    </small>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
};

const StatCard = ({ icon, title, value, description, loading = false }) => (
  <div
    className="card border-0 card-migration "
    style={{ 
      borderRadius: "8px", 
      height: "140px",
      minHeight: "140px",
      overflow: "hidden",
      width: "100%",
      marginBottom:"-6px"
    }}
  >
    <div className="card-body p-3">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <div className="d-flex align-items-center">
            {icon}
            <h5
              className="card-title fw-bold mb-0 dbcardsdesign"
              style={{
                height: "35px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {title}
            </h5>
          </div>
          {loading ? (
            <div className="spinner-border spinner-border-sm text-secondary"></div>
          ) : (
            <h4 className="mb-2 mt-2 homecardfont">{value || "NA"}</h4>
          )}
          <h6
            className="text-muted mb-1"
            style={{ fontSize: "12px", whiteSpace: "nowrap" }}
          >
            {description}
          </h6>
        </div>
      </div>
    </div>
  </div>
);

const JobCard = ({
  title,
  description,
  meta,
  time,
  status,
  sources,
  targets,
  transformations,
}) => {
  const truncateText = (text, maxLength) => {
    if (!text) return "NA";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const truncatedTitle = truncateText(title, 18);
  const truncatedDescription = truncateText(description, 25);

  return (
    <div
      className="card border-0 card-migration mb-1"
      style={{ 
        borderRadius: "8px",
        height: "166px",
        width: "100%"
      }}
    >
      <div className="card-body p-3">
        <div
          className="JobTitle"
          style={{  marginBottom: "4px" }}
        >
          {title && title.length > 20 ? (
            <CustomTooltip content={title} placement="top">
              <span style={{ cursor: "pointer" }}>{truncatedTitle}</span>
            </CustomTooltip>
          ) : (
            truncatedTitle
          )}
        </div>

        <div
          className="JobDesc job-desc"
          style={{ fontSize: "12px", marginBottom: "8px" }}
        >
          {description && description.length > 24 ? (
            <CustomTooltip content={description} placement="top">
              <span style={{ cursor: "pointer" }}>{truncatedDescription}</span>
            </CustomTooltip>
          ) : (
            truncatedDescription
          )}
        </div>

        <div className="job-desc"
          style={{
            fontSize: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#d1d5db",
                display: "inline-block",
              }}
            ></span>
            <span>{sources || "NA"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#d1d5db",
                display: "inline-block",
              }}
            ></span>
            <span>{targets || "NA"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#d1d5db",
                display: "inline-block",
              }}
            ></span>
            <span>{transformations || "NA"}</span>
          </div>
         <div style={{ display: "flex", alignItems: "center", gap: "6px",  whiteSpace: "nowrap" }}>
  <span
    style={{
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: "#d1d5db",
      display: "inline-block",
    }}
  ></span>
  <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
    {/* Completed: */}
    <i
      className="ri-calendar-2-line"
      style={{ fontSize: "14px" }}
    ></i>
    <span>{time || "NA"}</span>
  </span>
</div>

        </div>
      </div>
    </div>
  );
};

const SourceProfilerDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isScanTimelineFullscreen, setIsScanTimelineFullscreen] = useState(false);
  const [isRadarChartFullscreen, setIsRadarChartFullscreen] = useState(false);
const [isObjectComplexityFullscreen, setIsObjectComplexityFullscreen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scanTimelineContainerRef = useRef(null);
  const radarChartContainerRef = useRef(null);
const objectComplexityContainerRef = useRef(null);
  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 16000;


  useEffect(() => {
  let scrollTimeout;
  
  const handleScrollStart = () => {
    setIsScrolling(true);
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  };

  window.addEventListener('scroll', handleScrollStart, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', handleScrollStart);
    clearTimeout(scrollTimeout);
  };
}, []);
  const connectWebSocket = useCallback(() => {
    const access_token = sessionStorage.getItem("access_token");

    if (!access_token) {
      console.error("Authentication token missing");
      setLoading(false);
      return;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    const wsBaseUrl = process.env.REACT_APP_API_URL_CODE_SOURCE_PROFILER_WEB_SOCKET;
    const socketUrl = `${wsBaseUrl}/ws/get_profiler_dashboard?token=${access_token}`;

    socketRef.current = new WebSocket(socketUrl);

    socketRef.current.onopen = () => {
      console.log("WebSocket connected");
      retryDelayRef.current = 1000;
      setLoading(false);
      socketRef.current.send(JSON.stringify({ type: "get_profiler_dashboard" }));
    };

   socketRef.current.onmessage = (event) => {
  try {
    const message = JSON.parse(event.data);

    if (message.event === "profiler_dashboard") {
      const transformedData = {
        ...message.data,
        recent_jobs: message.data.recent_jobs || [],
        jobs_by_skill: message.data.jobs_by_skill || [],
        recent_activities: message.data.recent_activities || [],
        top_complex_jobs: message.data.top_complex_jobs || [],
        weekly_line_chart: message.data.weekly_line_chart || [],
      };

      // Only update if not currently scrolling
      if (!isScrolling) {
        setDashboardData(transformedData);
      }
    }
  } catch (err) {
    console.error("Error parsing WebSocket message:", err);
  }
};

    socketRef.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setLoading(false);
    };

    socketRef.current.onclose = (event) => {
      console.log("WebSocket disconnected:", event);
      if (!document.hidden) {
        setTimeout(connectWebSocket, retryDelayRef.current);
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, maxRetryDelay);
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && socketRef.current) {
        socketRef.current.close();
      } else if (
        !document.hidden &&
        (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED)
      ) {
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [connectWebSocket]);

  const toggleScanTimelineFullscreen = () => {
    if (!document.fullscreenElement) {
      scanTimelineContainerRef.current
        .requestFullscreen()
        .then(() => setIsScanTimelineFullscreen(true))
        .catch(console.error);
    } else {
      document
        .exitFullscreen()
        .then(() => setIsScanTimelineFullscreen(false))
        .catch(console.error);
    }
  };
  const toggleRadarChartFullscreen = () => {
  if (!document.fullscreenElement) {
    radarChartContainerRef.current
      .requestFullscreen()
      .then(() => setIsRadarChartFullscreen(true))
      .catch(console.error);
  } else {
    document
      .exitFullscreen()
      .then(() => setIsRadarChartFullscreen(false))
      .catch(console.error);
  }
};

const toggleObjectComplexityFullscreen = () => {
  if (!document.fullscreenElement) {
    objectComplexityContainerRef.current
      .requestFullscreen()
      .then(() => setIsObjectComplexityFullscreen(true))
      .catch(console.error);
  } else {
    document
      .exitFullscreen()
      .then(() => setIsObjectComplexityFullscreen(false))
      .catch(console.error);
  }
};
  
useEffect(() => {
  const handleFullscreenChange = () => {
    const isFullscreen = !!document.fullscreenElement;
    setIsScanTimelineFullscreen(isFullscreen && document.fullscreenElement === scanTimelineContainerRef.current);
    setIsRadarChartFullscreen(isFullscreen && document.fullscreenElement === radarChartContainerRef.current);
    setIsObjectComplexityFullscreen(isFullscreen && document.fullscreenElement === objectComplexityContainerRef.current);
  };

  document.addEventListener("fullscreenchange", handleFullscreenChange);
  return () => {
    document.removeEventListener("fullscreenchange", handleFullscreenChange);
  };
}, []);
  return (
  
  <div 
    className="container-fluid" 
    style={{ 
      padding:"6px 1.5px 20px 1.5px"
    }}
  >

      {/* <div className="row"> */}
        <div className="row g-2 mb-2">
        {/* Left Column */}
       <div 
  className="col-md-8"
  style={{
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)"
  }}
>
          <div className="row gx-2 gy-0" style={{marginBottom:"-2px"}}>
            {/* Row 1 */}
            <div className="col-md-4 mb-1">
              <StatCard
                icon={
                  <div className="icons-bg me-2">
                  <i className="dash-icon-total-jobs"></i>
              </div>
                }
                title="Total Jobs"
                value={dashboardData?.total_jobs || "NA"}
                description="Number of total jobs"
                loading={loading}
              />
            </div>
            <div className="col-md-4 mb-1">
              <StatCard
                icon={
                 <div className="icons-bg me-2">
                  <i className="dash-icon-source-system"></i>
              </div>
                }
                title="Source System Scans"
                value={dashboardData?.total_source_scans || "NA"}
                description="Number of source system scans"
                loading={loading}
              />
            </div>
            <div className="col-md-4 mb-3">
              <StatCard
                icon={
                  <div className="icons-bg me-2">
                  <i className="dash-icon-object-scans"></i>
              </div>
                }
                title="Object Scans"
                value={dashboardData?.total_objects || "NA"}
                description="Number of object scanned"
                loading={loading}
              />
            </div>
          </div>

          <div className="row gx-2 gy-0">            {/* Row 2 */}
            <div className="col-md-4 mb-1">
              <StatCard
                icon={
                  <div className="icons-bg me-2">
                  <i className="dash-icon-total-jobs"></i>
              </div>
                }
                title="Transformation Count"
                value={dashboardData?.total_transformations || "NA"}
                description="Transformation Count"
                loading={loading}
              />
            </div>
            <div className="col-md-4 mb-1">
              <StatCard
                icon={
                 <div className="icons-bg me-2">
                  <i className="dash-icon-source-system"></i>
              </div>
                }
                title="Avg Complexity"
                value={
                  dashboardData?.avg_complexity === "Null"
                    ? "NA"
                    : dashboardData?.avg_complexity
                }
                description="Avg Complexity Score"
                loading={loading}
              />
            </div>
            <div className="col-md-4 mb-1">
              <StatCard
                icon={
                  <div className="icons-bg me-2">
                  <i className="dash-icon-avg-timeout"></i>
              </div>
                }
                title="Avg Scan Time"
                value={dashboardData?.avg_scan_time || "NA"}
                description="Avg Scan Time"
                loading={loading}
              />
            </div>
          </div>

          {/* Most Recent Jobs */}
          <div className="row gx-2 gy-2 mt-1  ">           
             <div className="col-12"
                 style={{
    marginTop: dashboardData?.recent_jobs?.length > 0 ? "5px" : "5px", // or whatever height you want
     marginBottom: dashboardData?.recent_jobs?.length > 0 ? "-5px" : "-6px", // or whatever height you want
  }}
            
             >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center dbcardheadsdesign">
                  <div className="icons-bg me-2">
                  <i className="dash-icon-code-conversion"></i>
              </div>
                  <span >Most Recent Jobs</span>
                </div>
                {dashboardData?.recent_jobs?.length > 0 && (
                 <h5
                className="card-title fw-bold mb-0"
                style={{ fontSize: "11px", textDecoration: "underline" }}
              >
                <a
                  href="/sourceprofiler" 
                  style={{ color: "inherit", textDecoration: "inherit" }}
                >
                  View All
                </a>
              </h5>
                )}
              </div>
            </div>
            
            {dashboardData?.recent_jobs?.length > 0 ? (
              dashboardData.recent_jobs.slice(0, 3).map((job, idx) => (
                <div className="col-md-4 mb-3" key={`recent-job-${job.job_id || idx}`}>
                  <JobCard
                    title={job.job_name || "NA"}
                    description={job.job_description || "No description"}
                    sources={` ${job.sources || "NA"} Sources`}
                    targets={` ${job.targets || "NA"} Targets`}
                    transformations={` ${job.transformations || "NA"} Transformations`}
                    time={`${job.completed_at || "NA"}`}
                  />
                </div>
              ))
            ) : (
              <div className="col-12"
               style={{
    height: dashboardData?.recent_jobs?.length > 0 ? "auto" : "189px", // or whatever height you want
  }}
              >
              <div className="card border-0 card-migration" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
  <div className="card-body text-center p-3">
    <img
      src="assets/images/emptystate.gif"
      style={{ width: "145px", opacity: 0.7 }}
      alt="No Data"
    />
    <p className="text-muted mt-2 mb-0">No recent jobs available</p>
  </div>
</div>

              </div>
            )}
          </div>

          {/* Top Complex Jobs */}
          <div className="row gx-2 gy-1 mb-2 ">           
             <div className="col-12"
                style={{
   
    marginTop:dashboardData?.top_complex_jobs?.length > 0?"-10px":"16px",
  }}>
             
             
              <div className="d-flex align-items-center mb-2 dbcardheadsdesign">
                <div className="icons-bg me-2">
                  <i className="dash-icon-code-conversion"></i>
              </div>
                <span>Top Complex Jobs</span>
              </div>
            </div>
            
          <div style={{ display: "flex", flexDirection: "column"}}>
              {dashboardData?.top_complex_jobs?.length > 0 ? (
                dashboardData.top_complex_jobs.map((job, index) => (
                  <div className="card border-0 card-migration mb-2" key={index}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <div className="icons-bg2 me-2">
                              <i className="dash-icon-database"></i>
                          </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-0">{job.job_name || "NA"}</h6>
                        </div>
                        <div className="text-nowrap">
                          <span className="fw-medium">Total Files : </span>
                          <span>{job.total_files ?? 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="card border-0 card-migration "
                 style={{
    height: dashboardData?.top_complex_jobs?.length > 0 ? "auto" : "305px", // or desired fallback height
    
  }}
                >
                  <div className="card-body text-center p-4 "style={{alignItems:"center",justifyContent:"center"}}>
                    <img
                      src="assets/images/emptystate.gif"
                      style={{ width: "180px", opacity: 0.7 }}
                      alt="No Data"
                    />
                    <p className="text-muted mt-2 mb-0">No complex jobs available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scan Timeline */}
  <div
  className="card border-0 card-migration"
  style={{
    height:
      dashboardData?.weekly_line_chart?.length > 0
        ? isScanTimelineFullscreen
          ? "calc(100vh - 100px)"
          : "28.7%"
        : "31%", // fallback height if no timeline data
        marginTop:dashboardData?.weekly_line_chart?.length > 0 ? "-7px":"-24px",
    marginBottom: "2px",
    display: "flex",
    flexDirection: "column",
  }}
  ref={scanTimelineContainerRef}
>


          <div className="card-body p-3 d-flex flex-column" style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                height: "32px",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <h6 className="d-flex align-items-center dbcardheadsdesign">
                  <div className="icons-bg me-2">
                              <i className="dash-icon-database-migrated"></i>
                          </div>
                  <span >
                    Scan Timeline
                  </span>
                </h6>
              </div>
              <CustomTooltip
                content={
                  isScanTimelineFullscreen
                    ? "Exit fullscreen"
                    : "Enter fullscreen"
                }
                placement="left"
              >
                <i
                  className={`mdi ${
                    isScanTimelineFullscreen
                      ? "mdi-fullscreen-exit"
                      : "mdi-fullscreen"
                  } text-dark font-size-22 fullscreen`}
                  style={{ cursor: "pointer" }}
                  onClick={toggleScanTimelineFullscreen}
                />
              </CustomTooltip>
            </div>
            <div style={{ flex: 1, minHeight: "200px" }}>
              <SourceLineChart
                data={dashboardData?.weekly_line_chart || []}
                height={
                  isScanTimelineFullscreen ? "calc(100vh - 100px)" : "300px"
                }
              />
            </div>
          </div>
        </div>
        </div>

        {/* Right Column */}
 <div 
  className="col-md-4"
  style={{
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)"
  }}
>
   
          {/* Radar Chart */}
      <div
  className="card border-0 card-migration mb-3"
  style={{
    height: isRadarChartFullscreen ? "calc(100vh - 100px)" : (dashboardData?.jobs_by_skill?.length > 0 ? "504px" : "528px"),
  }}
  ref={radarChartContainerRef}
>
  <div className="card-body p-3 d-flex flex-column">
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
        height: "32px",
        flexShrink: 0,
      }}
    >
      <div className="d-flex align-items-center dbcardheadsdesign ">
        <div className="icons-bg me-2">
                              <i className="dash-icon-radar-chart"></i>
                          </div>
        <span>Jobs by Skills</span>
      </div>
      <CustomTooltip
        content={isRadarChartFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        placement="left"
      >
        <i
          className={`mdi ${
            isRadarChartFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
          } text-dark font-size-22 fullscreen`}
          style={{ cursor: "pointer" }}
          onClick={toggleRadarChartFullscreen}
        />
      </CustomTooltip>
    </div>
    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
      {dashboardData?.jobs_by_skill?.length > 0 ? (
        <RadarChartForConversion 
          data={dashboardData.jobs_by_skill} 
       
         
  height={isRadarChartFullscreen ? "70vh" : "300px"}

        />
      ) : (
        <EmptyState label="Radar Chart" />
      )}
    </div>
  </div>
</div>

          {/* Object Complexity */}
         <div 
  className="card border-0 card-migration mb-3" 
  style={{ 
    height: isObjectComplexityFullscreen ? "calc(100vh - 80px)" : (dashboardData?.top_complex_jobs?.length > 0 ? "481px" : "352px"),
    marginTop: dashboardData?.top_complex_jobs?.length > 0 ? "-6px" : "-8px" 
  }}
  ref={objectComplexityContainerRef}
>
  <div className="card-body p-3 d-flex flex-column">
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
        height: "32px",
        flexShrink: 0,
      }}
    >
      <div className="d-flex align-items-center dbcardheadsdesign">
        <div className="icons-bg me-2">
                              <i className="dash-icon-object-complexity"></i>
                          </div>
        <span>Object Complexity</span>
      </div>
      <CustomTooltip
        content={isObjectComplexityFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        placement="left"
      >
        <i
          className={`mdi ${
            isObjectComplexityFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
          } text-dark font-size-22 fullscreen`}
          style={{ cursor: "pointer" }}
          onClick={toggleObjectComplexityFullscreen}
        />
      </CustomTooltip>
    </div>
    <div 
      className="flex-grow-1 d-flex align-items-center justify-content-center"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%"
      }}
    >
      {dashboardData?.pie_chart ? (
        <SourcePieChart 
  data={dashboardData.pie_chart} 
 isFullscreen={isObjectComplexityFullscreen}
/>
      ) : (
        <EmptyState label="Object Complexity" />
      )}
    </div>
  </div>
</div>

          {/* Recent Activities */}
       <div
  style={{
    marginTop:
      dashboardData?.recent_activities &&
      dashboardData.recent_activities.length > 0
        ? "-7px"
        : "-8px", // or just leave it blank
        height:  dashboardData?.recent_activities &&
      dashboardData.recent_activities.length > 0 ?"28.7%":"31%"
  }}
>
  <RecentActivities
    activities={dashboardData?.recent_activities || []}
    loading={loading}
  />
</div>

        </div>
      </div>
    </div>
  );
};

export default SourceProfilerDashboard;